import { z } from "zod";
import { AuditableDomainEntitySchema, PaginationSchema } from "../types";
import { ProductType } from "@/constants/ProductType";
import { AppearanceType } from "@/constants/AppearanceType";

export const VoyageModelSchema = AuditableDomainEntitySchema.extend({
  name: z.string(),
  vessel: z.string(),
});

export type VoyageModel = z.infer<typeof VoyageModelSchema>;

export const ListVoyagesResponseSchema = z.object({
  pagination: PaginationSchema,
  data: z.array(VoyageModelSchema),
});

export type ListVoyagesResponse = z.infer<typeof ListVoyagesResponseSchema>;

export const ProductSampleSchema = z.object({
  appearance: z.nativeEnum(AppearanceType).nullable(),
  density: z.number().nullable(),
  flashPoint: z.number().nullable(),
  conductivity: z.number().nullable(),
  viscosity: z.number().nullable(),
  cloudPoint: z.number().nullable(),
  sulphur: z.number().nullable(),
  coldFilterPluggingPoint: z.number().nullable(),
  pourPoint: z.number().nullable(),
});

export type ProductSample = z.infer<typeof ProductSampleSchema>;

export const CertificatesOfQualityModelSchema = z.object({
  id: z.number(),
  name: z.string(),
  percentage: z.number(),
});

export const VoyageShipTankModelSchema = ProductSampleSchema.extend({
  voyageId: z.number(),
  shipTankId: z.number(),
  shipTankName: z.string(),
  product: z.nativeEnum(ProductType).nullable(),
  certificatesOfQuality: z.array(CertificatesOfQualityModelSchema),
});

export type VoyageShipTankModel = z.infer<typeof VoyageShipTankModelSchema>;

export const ListVoyageShipTanksResponseSchema = z.object({
  pagination: PaginationSchema,
  data: z.array(VoyageShipTankModelSchema),
});

export type ListVoyageShipTanksResponse = z.infer<
  typeof ListVoyagesResponseSchema
>;

export const VoyageCertificateOfQualityModelSchema = z
  .object({
    voyageId: z.number(),
    product: z.nativeEnum(ProductType),
    coqNumber: z.string(),
    initials: z.string(),
    density: z.number(),
    shipTanks: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
        percentage: z.number(),
      }),
    ),
  })
  .merge(AuditableDomainEntitySchema)
  .merge(ProductSampleSchema);

export type VoyageCertificateOfQualityModel = z.infer<
  typeof VoyageCertificateOfQualityModelSchema
>;

/**
 * A request to create a new voyage certificate of quality
 */
export const AddVoyageCertificateOfQualityModelSchema =
  ProductSampleSchema.extend({
    voyageId: z.number(),
    product: z.nativeEnum(ProductType),
    appearance: z.nativeEnum(AppearanceType).nullable(),
    filledShipTankIds: z.array(z.number()), // A list of Ids of ship tanks which will be 100% filled by this CoQ.
    coqNumber: z.string(),
    initials: z.string(),
  });

export type AddVoyageCertificateOfQualityModel = z.infer<
  typeof AddVoyageCertificateOfQualityModelSchema
>;

export const UpdateVoyageCertificateOfQualityModelSchema =
  ProductSampleSchema.extend({
    voyageId: z.number(),
    id: z.number().nullish(),
  });

export type UpdateVoyageCertificateOfQualityModel = z.infer<
  typeof UpdateVoyageCertificateOfQualityModelSchema
>;

export const ListVoyageCertificateOfQualityResponseSchema = z.object({
  pagination: PaginationSchema,
  data: z.array(VoyageCertificateOfQualityModelSchema),
});

/**
 * In the back end this is separated into add and edit
 * but at the present moment the requirements are the same.
 */
export const AddEditVoyageShipTankSchema = z.object({
  shipTankId: z.number().nullable(),
  voyageId: z.number().nullable(),
  certificatesOfQuality: z.array(
    z.object({
      id: z.number().nullable(),
      percentage: z.number(),
    }),
  ),
});

export type AddEditVoyageShipTank = z.infer<typeof AddEditVoyageShipTankSchema>;

export const ComputedCertificateOfQualityModelSchema =
  ProductSampleSchema.extend({
    product: z.nativeEnum(ProductType).nullable(),
  });

export type ComputedCertificateOfQuality = z.infer<
  typeof ComputedCertificateOfQualityModelSchema
>;
